import {
  getDefaultValues,
  getOptions,
  getValidationSchema,
} from 'app/components/Song/Details/SongDetailsForm/utils';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { ddlToOptions, getValidDirtyValues } from 'app/utils/utils';
import { updateSongEffect } from 'app/store/SongList/effects';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useAppDispatch } from 'app/store';
import { SongDetailsFormData, SongDetailsFormProps } from './types';

export const useSongDetailsFormData = ({
  song,
  validations,
  languageCodes,
  songDDL,
}: SongDetailsFormProps) => {
  const languages = getOptions(validations.language, languageCodes);
  const keySignatures = getOptions(
    validations.keySignature,
    songDDL.keySignatures
  );
  const genres = useMemo(() => ddlToOptions(songDDL.genres, true), [
    songDDL.genres,
  ]);
  const selectedGenres = useMemo(
    () => getOptions({ enum: song.genres || [] }, songDDL.genres, true),
    [song.genres, songDDL.genres]
  );

  return {
    languages,
    genres,
    keySignatures,
    selectedGenres,
  };
};

export const useSongDetailsForm = (props: SongDetailsFormProps) => {
  const { song, validations, languageCodes, songDDL } = props;
  const dispatch = useAppDispatch();

  const useFormProps = useForm<SongDetailsFormData>({
    defaultValues: getDefaultValues(song, languageCodes, songDDL),
    mode: 'all',
    shouldFocusError: false,
    resolver: yupResolver(getValidationSchema(validations, song)),
  });

  const { handleSubmit, formState, reset, getValues } = useFormProps;
  const { dirtyFields, errors } = formState;

  const submitHandler = async () => {
    const data = getValues();
    const validDirtyValues = getValidDirtyValues<SongDetailsFormData>(
      errors,
      dirtyFields,
      data
    );

    if (Object.keys(validDirtyValues).length) {
      reset(data, { keepErrors: true });
      try {
        await dispatch(
          updateSongEffect({ formData: validDirtyValues })
        ).unwrap();
      } catch (e) {
        dispatch(
          addAlertAction({
            message: 'Failed to save details. Please try again',
          })
        );
      }
    }
  };

  const onSubmit = handleSubmit(submitHandler, submitHandler);

  return { ...useFormProps, onSubmit };
};
